import { useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { calculateInstallments, calculateInstallmentAmount } from '../helpers';
import type {
  PaymentOption,
  MethodOfPayment,
  MethodOfPaymentFormStateKey,
} from '../types';

const INTEREST_RATE_FEATURE_FLAG = 'enable-interest-rate-enhancements';

interface UsePaymentCalculationsProps {
  carrierSettings?: PaymentOption;
  formStateKey: MethodOfPaymentFormStateKey;
  changeMethodOfPaymentAction: (payload: {
    name: string;
    value: string;
  }) => any;
}

export const usePaymentCalculations = ({
  carrierSettings,
  formStateKey,
  changeMethodOfPaymentAction,
}: UsePaymentCalculationsProps) => {
  const dispatch = useAppDispatch();
  const isInterestRateEnhancementEnabled = useFeatureFlag(
    INTEREST_RATE_FEATURE_FLAG,
  );

  const methodOfPayment = useAppSelector(
    (state) => state[formStateKey].data.method_of_payment,
  ) as MethodOfPayment | undefined;

  const billing = useAppSelector((state) => state[formStateKey].data.billing);

  // Calculate all derived fields
  const calculateDerivedFields = useCallback(() => {
    if (!methodOfPayment) return null;

    // Remove commas before parsing numbers
    const premium = parseFloat(
      (methodOfPayment.estimated_policy_premium || '0').replace(/,/g, ''),
    );

    // When enhancement enabled, use interest_amount/additional_charges (calculated dollar values)
    // When disabled (legacy), use interest field directly (user-entered dollar amount)
    const interestAmountStr = isInterestRateEnhancementEnabled
      ? (methodOfPayment as any).interest_amount ||
        (methodOfPayment as any).additional_charges ||
        '0'
      : methodOfPayment.interest || '0';
    const interest = parseFloat(interestAmountStr.replace(/,/g, ''));

    // Get tax amount (field name is 'tax' in both forms)
    const tax = parseFloat((methodOfPayment.tax || '0').replace(/,/g, ''));

    const amtPaid = parseFloat(
      (methodOfPayment.amt_paid_with_application || '0').replace(/,/g, ''),
    );

    const paymentPlan =
      methodOfPayment.type_of_payment_plan ||
      billing?.payment_schedule ||
      'annual';

    // Calculate total cost (premium + additional charges + tax)
    const totalCost = premium + interest + tax;

    // Calculate amount still due
    const amountDue = totalCost - amtPaid;

    // Use existing installments value if set, otherwise calculate
    const numInstallments = methodOfPayment.no_of_remaining_instalments
      ? parseInt(methodOfPayment.no_of_remaining_instalments, 10)
      : calculateInstallments(paymentPlan, carrierSettings);

    // Calculate installment amount
    const installmentAmount = calculateInstallmentAmount(
      amountDue,
      numInstallments,
    );

    return {
      total_estimated_cost: totalCost.toFixed(2),
      amount_still_due: amountDue.toFixed(2),
      no_of_remaining_instalments: numInstallments.toString(),
      amount_of_each_instalment: installmentAmount,
    };
  }, [
    methodOfPayment,
    billing?.payment_schedule,
    carrierSettings,
    isInterestRateEnhancementEnabled,
  ]);

  // Update Redux with calculated values
  const updateCalculatedFields = useCallback(
    (fields: Record<string, string>) => {
      Object.entries(fields).forEach(([name, value]) => {
        // Only dispatch if value has changed
        if (methodOfPayment?.[name as keyof typeof methodOfPayment] !== value) {
          dispatch(changeMethodOfPaymentAction({ name, value }));
        }
      });
    },
    [dispatch, methodOfPayment, changeMethodOfPaymentAction],
  );

  // Calculate and update on mount and when dependencies change
  useEffect(() => {
    if (!methodOfPayment?.estimated_policy_premium) return;

    const calculatedFields = calculateDerivedFields();
    if (calculatedFields) {
      updateCalculatedFields(calculatedFields);
    }
  }, [
    // Only recalculate when inputs change
    methodOfPayment,
    billing?.payment_schedule,
    carrierSettings,
    calculateDerivedFields,
    updateCalculatedFields,
  ]);
};
